// =============================================================================
// src/lib/scheduleCore.js - pure scheduling helpers (no Supabase imports, so
// the contract test runs under plain node).
//
// Booking rules live in the database (migration 20261010100000_schedule_safety):
//   * trg_appt_guard_schedule blocks a provider double-booking unless it is
//     saved as an overbook with a reason
//   * schedule_check_slot() returns the warnings shown before saving
// These helpers turn that into what staff read.
// =============================================================================

// Statuses that hold time on the schedule (server: appt_holds_slot).
export const FREE_STATUSES = ["Cancelled", "Rescheduled", "No Show"];
export const holdsSlot = (status) => !FREE_STATUSES.includes(status || "");

// Statuses a visit can be dragged / moved from.
export const MOVABLE_STATUSES = ["Scheduled", "Confirmed", "Held"];
export const isMovable = (a) => !!a && MOVABLE_STATUSES.includes(a.status) && !a.checked_in_at;

export const STATUSES = ["Scheduled", "Confirmed", "Held", "Checked In", "Roomed", "In Progress", "Completed", "No Show", "Cancelled", "Rescheduled"];

export const CANCEL_REASONS = [
  "Patient asked to cancel",
  "Patient is sick",
  "No transportation",
  "Provider unavailable",
  "Weather or office closure",
  "Insurance or coverage issue",
  "Booked in error",
  "Other",
];

export const OVERBOOK_REASONS = [
  "Urgent / same-day need",
  "Quick visit (shot, BP check, results)",
  "Provider approved",
  "Patient traveling far",
  "Likely no-show (overbook tip)",
  "Other",
];

// 15-minute slot -> "9:30 AM".
export function slotLabel(slot) {
  if (!Number.isFinite(slot)) return "";
  const mins = slot * 15;
  const h = Math.floor(mins / 60) % 24, m = mins % 60;
  return ((h + 11) % 12 + 1) + ":" + String(m).padStart(2, "0") + (h < 12 ? " AM" : " PM");
}

// Local calendar date "YYYY-MM-DD" (never UTC: after 8 PM Eastern the UTC
// date is already tomorrow).
export function localYmd(d = new Date()) {
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

// Current 15-minute slot in local time.
export function localNowSlot(d = new Date()) {
  return d.getHours() * 4 + Math.floor(d.getMinutes() / 15);
}

// Walk-ins start "now" (rounded down), never at midnight.
export function walkInSlot(d = new Date(), dur = 2) {
  return Math.max(0, Math.min(96 - dur, localNowSlot(d)));
}

// Patient search text that is safe inside a PostgREST or() filter: commas,
// parentheses, wildcards and quotes would break or widen the filter.
export function escapeSearch(s) {
  return String(s || "").replace(/[,()*%\\:"'.]/g, " ").replace(/\s+/g, " ").trim().slice(0, 60);
}

// "Dr. Jane Smith" for physicians, "Jane Smith, NP" for everyone else.
const DOCTOR = /^(MD|DO|DPM|DDS|DMD|PHD|PSYD|OD|DC|DNP|PHARMD|AUD)\b/i;
export function providerLabel(p) {
  if (!p) return "";
  const name = [p.first_name, p.last_name].filter(Boolean).join(" ");
  const cred = String(p.credential || "").replace(/\./g, "").trim();
  if (cred && DOCTOR.test(cred)) return "Dr. " + name;
  return cred ? name + ", " + p.credential : name;
}

export const isTelehealthType = (name) => /tele|video|virtual/i.test(String(name || ""));

// Day grid bounds: practice hours, widened to show any visit booked outside
// them (legacy walk-ins at slot 0 are listed separately, not stretched in).
export function gridBounds(dayHours, appts) {
  const open = dayHours && dayHours.is_open !== false && Number.isFinite(dayHours.open_slot) ? dayHours.open_slot : 28;
  const close = dayHours && dayHours.is_open !== false && Number.isFinite(dayHours.close_slot) ? dayHours.close_slot : 72;
  let start = open, end = close;
  for (const a of appts || []) {
    if (!Number.isFinite(a.start_slot) || isLegacyWalkIn(a)) continue;
    start = Math.min(start, a.start_slot);
    end = Math.max(end, a.start_slot + (a.duration_slots || 1));
  }
  start = Math.floor(start / 4) * 4;
  end = Math.min(96, Math.ceil(end / 4) * 4);
  return { start, end, closed: !!(dayHours && dayHours.is_open === false) };
}

export const isLegacyWalkIn = (a) => !!a && a.source === "Walk-in" && (a.start_slot || 0) === 0;

// Side-by-side lanes for overlapping visits in one provider column, so an
// overbook never hides the visit under it. Returns Map id -> { lane, lanes }.
export function layoutLanes(appts) {
  const rows = (appts || []).filter((a) => Number.isFinite(a.start_slot))
    .slice().sort((a, b) => a.start_slot - b.start_slot || (b.duration_slots || 1) - (a.duration_slots || 1));
  const out = new Map();
  let group = [], groupEnd = -1;
  const flush = () => {
    const laneEnds = [];
    for (const a of group) {
      let lane = laneEnds.findIndex((end) => end <= a.start_slot);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(0); }
      laneEnds[lane] = a.start_slot + (a.duration_slots || 1);
      out.set(a.id, { lane, lanes: 0 });
    }
    for (const a of group) out.get(a.id).lanes = laneEnds.length;
    group = []; groupEnd = -1;
  };
  for (const a of rows) {
    const end = a.start_slot + (a.duration_slots || 1);
    if (group.length && a.start_slot >= groupEnd) flush();
    group.push(a);
    groupEnd = Math.max(groupEnd, end);
  }
  if (group.length) flush();
  return out;
}

// schedule_check_slot() result -> what to show before saving.
//   blocking: provider conflicts (needs an overbook reason)
//   warnings: things staff should see and accept
export function slotWarnings(check) {
  if (!check) return { conflicts: [], warnings: [], needsConfirm: false };
  const conflicts = check.conflicts || [];
  const w = [];
  if (check.provider_inactive) w.push("This provider is inactive.");
  const own = check.hours_source === "provider";
  if (check.holiday) w.push("The practice is closed for " + check.holiday + ".");
  else if (check.closed_day) w.push(own ? "This provider is not working this day." : "The practice is closed this day.");
  else if (check.outside_hours) w.push((own ? "Outside this provider's hours (" : "Outside practice hours (") + slotLabel(check.open_slot) + " to " + slotLabel(check.close_slot) + ").");
  if (check.blocked) w.push("This time is blocked: " + check.blocked + ".");
  if (check.past) w.push("This time has already passed.");
  if (check.patient_status) w.push("This patient's chart is " + String(check.patient_status).toLowerCase() + ".");
  for (const b of check.patient_busy || []) {
    w.push("The patient is already booked at " + slotLabel(b.start_slot) + (b.provider ? " with " + b.provider : "") + ".");
  }
  const sameDay = check.patient_same_day || [];
  if (sameDay.length) {
    w.push("The patient has another visit this day at " + sameDay.map((s) => slotLabel(s.start_slot)).join(", ") + ".");
  }
  return { conflicts, warnings: w, needsConfirm: conflicts.length > 0 || w.length > 0 };
}

export function conflictLine(c) {
  return slotLabel(c.start_slot) + " " + (c.patient || "Blocked time") + (c.appt_type ? " (" + c.appt_type + ")" : "");
}

export function scheduleError(e) {
  const m = String((e && e.message) || e || "");
  if (m.includes("schedule_conflict")) return "That time is already booked for this provider. Pick another time or book it as an overbook with a reason.";
  if (m.includes("overbook_reason_required")) return "Add a reason for the overbook.";
  if (m.includes("slot_taken")) return "That slot was just booked by someone else. Pick another opening.";
  if (m.includes("slot_past")) return "That slot has already passed.";
  if (m.includes("patient_busy")) return "This patient is already booked at that time.";
  if (m.includes("patient_inactive")) return "This patient's chart is not active.";
  if (m.includes("offer_closed")) return "That offer has expired.";
  if (m.includes("not_authorized")) return "You don't have permission to do that.";
  if (m.includes("appointments_slot_range_chk")) return "Pick a time that ends by midnight.";
  return m || "Something went wrong.";
}

// --- Calendar v2 (week / list views, openings, quick actions) ---------------

export function addDays(ymdStr, n) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymdStr || ""));
  if (!m) return ymdStr;
  return localYmd(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n));
}
export const dowOf = (ymdStr) => new Date(ymdStr + "T12:00:00").getDay();

// Sunday-start week containing the date: 7 "YYYY-MM-DD" strings.
export function weekDays(ymdStr) {
  const start = addDays(ymdStr, -dowOf(ymdStr));
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

export function isHoliday(holidays, ymdStr) {
  const md = String(ymdStr).slice(5);
  return (holidays || []).find((h) => h.holiday_date === ymdStr || (h.is_recurring && String(h.holiday_date).slice(5) === md)) || null;
}

// Next open times. Pure: the caller passes the appointments it loaded for
// the range. Skips closed days and holidays, past times today, and any
// time that would overlap a visit that holds its slot. At most perDay
// openings per provider per day so the list spans more than one morning.
//   part: "any" | "morning" (before noon) | "afternoon"
// Working hours for one provider on one date, the same rules as the
// database's provider_day_hours(): holiday closes; the provider's own row for
// that weekday wins; else the practice's.
//   -> { is_open, open_slot, close_slot, source }
export function dayHoursFor(providerId, ymdStr, { hours, providerHours, holidays } = {}) {
  if (isHoliday(holidays, ymdStr)) return { is_open: false, open_slot: null, close_slot: null, source: "holiday" };
  const dow = dowOf(ymdStr);
  const own = (providerHours || []).find((x) => x.provider_id === providerId && x.day_of_week === dow);
  if (own) return { is_open: own.is_open !== false, open_slot: own.open_slot, close_slot: own.close_slot, source: "provider" };
  const h = (hours || []).find((x) => x.day_of_week === dow);
  if (h) return { is_open: h.is_open !== false, open_slot: h.open_slot, close_slot: h.close_slot, source: "practice" };
  return { is_open: false, open_slot: null, close_slot: null, source: "none" };
}

// Blocks that apply to a provider on a date (practice-wide blocks too), the
// same rules as schedule_block_at(): one day, or weekly from block_date until
// repeat_until.
export function blocksOn(blocks, providerId, ymdStr) {
  const dow = dowOf(ymdStr);
  return (blocks || []).filter((b) => (!b.provider_id || b.provider_id === providerId)
    && (b.block_date === ymdStr || (b.repeat_weekly && ymdStr > b.block_date && dowOf(b.block_date) === dow
        && (!b.repeat_until || ymdStr <= b.repeat_until))));
}
export function blockAt(blocks, providerId, ymdStr, start, dur = 1) {
  return blocksOn(blocks, providerId, ymdStr).find((b) => start < b.end_slot && start + Math.max(dur || 1, 1) > b.start_slot) || null;
}

// Next open times. Pure: the caller passes the appointments it loaded for
// the range. Skips holidays, days and times the provider is not working
// (their own hours, else the practice's), blocked time, past times today and
// any time that would overlap a visit that holds its slot. At most perDay
// openings per provider per day so the list spans more than one morning.
//   part: "any" | "morning" (before noon) | "afternoon"
export function findOpenings({ providers, appts, hours, providerHours, blocks, holidays, fromDate, days = 14, dur = 2, part = "any", limit = 12, perDay = 3, now = new Date(), step = 2 }) {
  const out = [];
  const today = localYmd(now);
  const nowSlot = localNowSlot(now);
  for (let i = 0; i < days && out.length < limit; i++) {
    const date = addDays(fromDate, i);
    if (date < today) continue;
    for (const p of providers || []) {
      const h = dayHoursFor(p.id, date, { hours, providerHours, holidays });
      if (!h.is_open || !Number.isFinite(h.open_slot) || !Number.isFinite(h.close_slot)) continue;
      const busy = (appts || []).filter((a) => a.provider_id === p.id && a.appt_date === date && holdsSlot(a.status)
        && Number.isFinite(a.start_slot) && !isLegacyWalkIn(a) && a.source !== "Walk-in");
      const dayBlocks = blocksOn(blocks, p.id, date);
      let n = 0;
      for (let s = h.open_slot; s + dur <= h.close_slot && n < perDay; s += step) {
        if (date === today && s <= nowSlot) continue;
        if (part === "morning" && s >= 48) continue;
        if (part === "afternoon" && s < 48) continue;
        const clash = busy.some((a) => s < a.start_slot + (a.duration_slots || 1) && s + dur > a.start_slot)
          || dayBlocks.some((b) => s < b.end_slot && s + dur > b.start_slot);
        if (clash) continue;
        out.push({ date, start_slot: s, duration_slots: dur, provider_id: p.id });
        n++;
      }
    }
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.start_slot - b.start_slot)).slice(0, limit);
}

// Weekly repeat text for a block.
export function blockRepeatText(b) {
  if (!b || !b.repeat_weekly) return "One day";
  const day = new Date(b.block_date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long" });
  return "Every " + day + (b.repeat_until ? " until " + new Date(b.repeat_until + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");
}

// Header numbers for a day's visits.
export function daySummary(appts) {
  const list = appts || [];
  const live = list.filter((a) => holdsSlot(a.status));
  return {
    booked: live.length,
    arrived: list.filter((a) => a.checked_in_at || ["Checked In", "Roomed", "In Progress", "Completed"].includes(a.status)).length,
    noShows: list.filter((a) => a.status === "No Show").length,
    unconfirmed: live.filter((a) => ["Scheduled"].includes(a.status) && !a.confirmed_at && a.confirmation_status !== "Confirmed").length,
    overbooks: live.filter((a) => a.overbooked).length,
    cancelled: list.filter((a) => a.status === "Cancelled").length,
  };
}

// Which one-tap actions fit this visit right now.
export function quickActions(a, { today = localYmd(), nowSlot = localNowSlot() } = {}) {
  if (!a) return [];
  const out = [];
  const isToday = a.appt_date === today;
  const started = a.appt_date < today || (isToday && Number.isFinite(a.start_slot) && a.start_slot <= nowSlot);
  const booked = ["Scheduled", "Confirmed"].includes(a.status) && !a.checked_in_at;
  if (a.status === "Held") out.push("confirm_hold");
  if (booked && a.status === "Scheduled" && !a.confirmed_at && a.confirmation_status !== "Confirmed") out.push("confirm");
  if (booked && isToday && !isTelehealthType(a.appt_type)) out.push("check_in");
  if (booked && started) out.push("no_show");
  if (isMovable(a)) out.push("reschedule");
  if (holdsSlot(a.status) && !a.checked_in_at && !["Completed", "In Progress", "Roomed"].includes(a.status)) out.push("cancel");
  return out;
}

// ?date=YYYY-MM-DD&view=week&appt=<uuid>
export function parseScheduleQuery(search) {
  const q = new URLSearchParams(search || "");
  const date = q.get("date");
  const view = q.get("view");
  const appt = q.get("appt");
  return {
    date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null,
    view: ["day", "week", "month", "list"].includes(view) ? view : null,
    appt: appt && /^[0-9a-f-]{36}$/i.test(appt) ? appt : null,
  };
}

export function checkInError(e) {
  const m = String((e && e.message) || e || "");
  if (m.includes("not_today")) return "Only today's visits can be checked in.";
  if (m.includes("not_available")) return "This visit can't be checked in from its current status.";
  if (m.includes("telehealth")) return "Telehealth visits are joined from the chart, not checked in.";
  return scheduleError(e);
}

// --- One status language (Track 3) ------------------------------------------
// Every screen that shows a visit's state reads it from here, so a visit is
// "Checked in" (amber) on the Schedule, the dashboards, the chart and the
// Queue alike. Variants are Badge variants.
//   triage: today's front desk view - an unconfirmed visit reads amber.
//   patient: portal wording (no staff jargon, no "Unconfirmed").
export function apptState(a, { triage = false, patient = false } = {}) {
  if (!a) return { key: "unknown", label: "", variant: "neutral" };
  const s = a.status;
  if (s === "Cancelled") return { key: "cancelled", label: "Cancelled", variant: "neutral" };
  if (s === "Rescheduled") return { key: "rescheduled", label: patient ? "Moved" : "Rescheduled", variant: "neutral" };
  if (s === "No Show") return { key: "no_show", label: patient ? "Missed" : "No-show", variant: "red" };
  if (s === "Completed" || a.completed_at) return { key: "done", label: patient ? "Completed" : "Done", variant: "green" };
  if (s === "Held") return { key: "held", label: patient ? "Reserved for you" : "Held", variant: "purple" };
  if (s === "In Progress" || a.seen_at) return { key: "in_visit", label: patient ? "In your visit" : "In visit", variant: "purple" };
  if (s === "Roomed" || a.roomed_at) return { key: "roomed", label: patient ? "In a room" : "Roomed", variant: "blue" };
  if (s === "Checked In" || a.checked_in_at) return { key: "checked_in", label: "Checked in", variant: "amber" };
  if (a.confirmation_status === "Declined") return { key: "declined", label: patient ? "Scheduled" : "Declined", variant: patient ? "neutral" : "red" };
  if (s === "Confirmed" || a.confirmed_at || a.confirmation_status === "Confirmed") return { key: "confirmed", label: "Confirmed", variant: "teal" };
  if (patient) return { key: "scheduled", label: "Scheduled", variant: "neutral" };
  return triage ? { key: "unconfirmed", label: "Unconfirmed", variant: "amber" } : { key: "scheduled", label: "Scheduled", variant: "neutral" };
}

// CHW visits / CM touchpoints / care conferences use their own status words
// (NoShow, Missed); same colors as clinic visits.
const VISIT_STATUS = {
  Scheduled: { label: "Scheduled", variant: "neutral" },
  Confirmed: { label: "Confirmed", variant: "teal" },
  Completed: { label: "Done", variant: "green" },
  NoShow: { label: "No-show", variant: "red" },
  "No Show": { label: "No-show", variant: "red" },
  Missed: { label: "Missed", variant: "red" },
  Cancelled: { label: "Cancelled", variant: "neutral" },
  Rescheduled: { label: "Rescheduled", variant: "neutral" },
  Held: { label: "Held", variant: "purple" },
};
export const visitStatusMeta = (s) => VISIT_STATUS[s] || { label: s || "Scheduled", variant: "neutral" };

// Who may add or remove blocked time (matches the schedule_blocks RLS policy)
// and edit provider hours (Owner / Manager).
export const BLOCK_ROLES = ["Owner", "Manager", "Front Desk"];
export const HOURS_ROLES = ["Owner", "Manager"];
export const BLOCK_REASONS = ["Lunch", "Meeting", "Out of office", "Admin time", "Training", "Other"];

// Rides block 4 (20270117100000_ride_block4): a visit's office. The
// provider's primary location when it is active, else the practice's primary
// (or first) active location. The Location picker shows only with 2+.
export function defaultLocationId(locations, provider) {
  const list = (locations || []).filter((l) => l && l.is_active !== false);
  if (!list.length) return "";
  const own = provider && provider.primary_location_id && list.find((l) => l.id === provider.primary_location_id);
  if (own) return own.id;
  const primary = list.find((l) => l.is_primary);
  return (primary || list[0]).id;
}
export function showLocationPicker(locations) {
  return (locations || []).filter((l) => l && l.is_active !== false).length > 1;
}
